import { Option } from "effect";
import type { Document, Html, HtmlBuilder } from "foldkit/html";
import type { View as SubmodelView } from "foldkit/submodel";

import { Auth } from "../auth/model";
import type { ClerkMounts } from "../auth/mounts";
import { i18next } from "../i18n";
import { PageMessage, PageModel, pages } from "../pages";
import type { Shared } from "../pages/shared";
import * as LogInteraction from "../widgets/logInteraction";
import { Message } from "./message";
import { type Model, toShared } from "./model";
import { creditsHref, drawerToggleId, nav, sidebar } from "./nav";
import { timeTravelBanner } from "./timeTravel";

const embed =
	(h: HtmlBuilder<Message>, shared: Shared) =>
	<M, Msg>(
		slotId: string,
		view: SubmodelView<M, Msg, Shared>,
		model: M,
		wrapMessage: (fields: { message: Msg }) => PageMessage,
	): Html =>
		h.submodel({
			slotId,
			view,
			model,
			viewInputs: shared,
			toParentMessage: (message: Msg) =>
				Message.GotPageMessage({ message: wrapMessage({ message }) }),
		});

const activePage = (
	page: PageModel,
	shared: Shared,
	h: HtmlBuilder<Message>,
): Html => {
	const sub = embed(h, shared);
	return PageModel.match(page, {
		Home: ({ model }) => sub("Home", pages.Home.view, model, PageMessage.Home),
		Records: ({ model }) =>
			sub("Records", pages.Records.view, model, PageMessage.Records),
		Record: ({ model }) =>
			sub("Record", pages.Record.view, model, PageMessage.Record),
		Entry: ({ model }) =>
			sub("Entry", pages.Entry.view, model, PageMessage.Entry),
		Settings: ({ model }) =>
			sub("Settings", pages.Settings.view, model, PageMessage.Settings),
		Credits: ({ model }) =>
			sub("Credits", pages.Credits.view, model, PageMessage.Credits),
		Rates: ({ model }) =>
			sub("Rates", pages.Rates.view, model, PageMessage.Rates),
		Developer: ({ model }) =>
			sub("Developer", pages.Developer.view, model, PageMessage.Developer),
		Developers: ({ model }) =>
			sub("Developers", pages.Developers.view, model, PageMessage.Developers),
		Admin: ({ model }) =>
			sub("Admin", pages.Admin.view, model, PageMessage.Admin),
		Audit: ({ model }) =>
			sub("Audit", pages.Audit.view, model, PageMessage.Audit),
		AI: ({ model }) => sub("AI", pages.AI.view, model, PageMessage.AI),
		Chat: ({ model }) => sub("Chat", pages.Chat.view, model, PageMessage.Chat),
		Integrations: ({ model }) =>
			sub(
				"Integrations",
				pages.Integrations.view,
				model,
				PageMessage.Integrations,
			),
		KeepInTouch: ({ model }) =>
			sub(
				"KeepInTouch",
				pages.KeepInTouch.view,
				model,
				PageMessage.KeepInTouch,
			),
		Tags: ({ model }) => sub("Tags", pages.Tags.view, model, PageMessage.Tags),
		AttributeSchema: ({ model }) =>
			sub(
				"AttributeSchema",
				pages.AttributeSchema.view,
				model,
				PageMessage.AttributeSchema,
			),
		Import: ({ model }) =>
			sub("Import", pages.Import.view, model, PageMessage.Import),
		Capture: ({ model }) =>
			sub("Capture", pages.Capture.view, model, PageMessage.Capture),
		Tasks: ({ model }) =>
			sub("Tasks", pages.Tasks.view, model, PageMessage.Tasks),
		Task: ({ model }) => sub("Task", pages.Task.view, model, PageMessage.Task),
		Knowledge: ({ model }) =>
			sub("Knowledge", pages.Knowledge.view, model, PageMessage.Knowledge),
		Note: ({ model }) => sub("Note", pages.Note.view, model, PageMessage.Note),
		Agents: ({ model }) =>
			sub("Agents", pages.Agents.view, model, PageMessage.Agents),
		NotFound: ({ model }) =>
			sub("NotFound", pages.NotFound.view, model, PageMessage.NotFound),
	});
};

/** The page shows which limit it hit; this offers Billing, and only where Billing exists (flag on). */
const limitNotice = (model: Model, h: HtmlBuilder<Message>): Html =>
	model.limitNoticeOpen && model.billingPreviewEnabled
		? h.div(
				[
					h.Role("status"),
					h.Class("alert alert-warning rounded-none border-x-0 border-t-0"),
				],
				[
					h.span([], [i18next.t("limits.banner")]),
					h.div(
						[h.Class("flex gap-2")],
						[
							h.a(
								[h.Href(creditsHref(model)), h.Class("btn btn-sm btn-primary")],
								[i18next.t("limits.seePlans")],
							),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-sm btn-ghost"),
									h.AriaLabel(i18next.t("limits.dismiss")),
									h.OnClick(Message.ClickedDismissLimitNotice()),
								],
								["✕"],
							),
						],
					),
				],
			)
		: h.empty;

/** Signed-in frame with navigation and the active page. */
const shell = (
	model: Model,
	mounts: ClerkMounts,
	h: HtmlBuilder<Message>,
): Html =>
	Option.match(Option.all([model.page, toShared(model)]), {
		onNone: () =>
			h.div([h.Class("min-h-screen")], [i18next.t("common.loading")]),
		onSome: ([page, shared]) =>
			h.div(
				// U16: from lg the drawer is the fixed sidebar; below it, the phone drawer.
				[h.Class("drawer lg:drawer-open")],
				[
					h.input([
						h.Id(drawerToggleId),
						h.Type("checkbox"),
						h.Checked(model.sidebar.drawerOpen),
						h.Class("drawer-toggle"),
						h.AriaLabel(i18next.t("layout.toggleDrawer")),
					]),
					// Before the content so Tab reaches the nav before the page body;
					// DaisyUI places both by grid column, and `toggle ~ side` still matches.
					sidebar(model, mounts, h),
					h.div(
						[h.Class("drawer-content flex min-w-0 flex-col")],
						[
							nav(model, h),
							h.submodel({
								slotId: "log-interaction",
								model: model.logInteraction,
								view: LogInteraction.view,
								viewInputs: shared,
								toParentMessage: (message: LogInteraction.Message) =>
									Message.GotLogInteractionMessage({ message }),
							}),
							timeTravelBanner(model, h),
							limitNotice(model, h),
							h.main(
								[h.Class("relative p-4")],
								[
									h.div(
										[h.Class("min-h-[calc(100vh-4rem)]")],
										[activePage(page, shared, h)],
									),
								],
							),
						],
					),
				],
			),
	});

export const makeView =
	(mounts: ClerkMounts) =>
	(model: Model, h: HtmlBuilder<Message>): Document => ({
		title: "Rhysomn",
		body: Auth.match(model.auth, {
			ClerkLoading: () =>
				h.div([h.DataAttribute("testid", "clerk-loading")], []),
			SignedOut: () =>
				h.div(
					[h.Class("min-h-screen flex items-center justify-center")],
					[
						h.div(
							[
								h.DataAttribute("testid", "sign-in-host"),
								h.OnMount(mounts.SignIn()),
							],
							[],
						),
					],
				),
			SignedIn: () => shell(model, mounts, h),
		}),
	});
